import { store } from 'createStore';
import * as go from 'gojs';
import { checkFeatureFlag, FeatureFlag } from 'hooks/useFeatureFlag';
import { addConnection } from 'store/connections/sagaActions';
import {
  setOpenPanel,
  setShowReportPanel,
  setShowSideCard,
} from 'store/layout/reducer';
import {
  clearSelectedNodes,
  selectNode,
  setActiveUnit,
  setActiveUnitError,
  setAdditionalActiveUnitError,
} from 'store/units/reducer';
import { addUnit } from 'store/units/sagaActions';
import type { GoJSInterface } from 'store/units/types';
import { getPortsForUnit } from 'store/units/unitRegistry';
import { getUnitInfoData } from 'util/getUnitInfoData';
import { v4 as uuidv4 } from 'uuid';
import {
  createCanvasModalBlocker,
  lockCanvasForModal,
} from '../../../diagram/canvasModalLock';
import {
  HOVER_HIDE_DELAY_MS,
  HOVER_HIT_PADDING,
  SMART_SELECT_PLACEMENT_PREVIEW_IMAGE_NAME,
} from './smartSelectConstants';
import {
  clampPointToViewport,
  containsPointWithPadding,
  getArrowPoints,
  getPlacementArrowPoints,
  getRectCenter,
  getSmartSelectPanelDimensions,
  isRealPoint,
} from './smartSelectGeometry';
import {
  createFreePortArrowLink,
  createSmartSelectPlacementArrow,
  createSmartSelectPlacementPreview,
} from './smartSelectOverlays';
import { createSmartSelectPanelView } from './smartSelectPanelView';
import { getPortData, isSmartSelectableOutputPort } from './smartSelectPorts';
import { loadSmartSelectRecommendations } from './smartSelectRecommendationLoader';
import type { SmartSelectRecommendedOption } from './smartSelectRecommendations';
import {
  clearTimer,
  hideTimers,
  nodeSmartSelectPanels,
  nodeSmartSelectSelectedOptions,
  nodeSmartSelectSelectedPortKeys,
} from './smartSelectState';
import { createSmartSelectUnitInfo } from './smartSelectUnitInfo';

const hoveredNodes = new WeakSet<go.Node>();

const nodeArrows = new WeakMap<go.Node, go.Link[]>();

const nodeSmartSelectButtons = new WeakMap<go.Node, go.Part>();

const nodeSmartSelectModalBlockers = new WeakMap<go.Node, go.Part>();

const nodeSmartSelectPlacementPreviews = new WeakMap<
  go.Node,
  Map<string, go.Part>
>();

const nodeSmartSelectPlacementArrows = new WeakMap<
  go.Node,
  Map<string, go.Link>
>();

const nodeSmartSelectPorts = new WeakMap<go.Node, string>();

const nodeSmartSelectModalLockReleases = new WeakMap<go.Node, () => void>();

const smartSelectPanelDragOffsets = new WeakMap<go.Part, go.Point>();

const removeSmartSelectButton = (node: go.Node | null | undefined) => {
  if (!node) {
    return;
  }

  const button = nodeSmartSelectButtons.get(node);

  if (!button) {
    return;
  }

  button.diagram?.remove(button);
  nodeSmartSelectButtons.delete(node);
  nodeSmartSelectPorts.delete(node);
};

const removeSmartSelectPlacementArrow = (node: go.Node | null | undefined) => {
  if (!node) {
    return;
  }

  const arrows = nodeSmartSelectPlacementArrows.get(node);

  if (!arrows) {
    return;
  }

  arrows.forEach((arrow) => arrow.diagram?.remove(arrow));

  nodeSmartSelectPlacementArrows.delete(node);
};

const removeSmartSelectPlacementPreview = (
  node: go.Node | null | undefined,
) => {
  if (!node) {
    return;
  }

  removeSmartSelectPlacementArrow(node);

  const previews = nodeSmartSelectPlacementPreviews.get(node);

  if (!previews) {
    return;
  }

  previews.forEach((preview) => preview.diagram?.remove(preview));

  nodeSmartSelectPlacementPreviews.delete(node);
};

const removeSmartSelectPlacementForPort = (node: go.Node, portKey: string) => {
  const arrow = nodeSmartSelectPlacementArrows.get(node)?.get(portKey);
  const preview = nodeSmartSelectPlacementPreviews.get(node)?.get(portKey);
  const diagram = node.diagram;

  if (diagram) {
    diagram.commit(() => {
      if (arrow) {
        diagram.remove(arrow);
      }
      if (preview) {
        diagram.remove(preview);
      }
    }, 'remove smart select output placement');
  }

  nodeSmartSelectPlacementArrows.get(node)?.delete(portKey);
  nodeSmartSelectPlacementPreviews.get(node)?.delete(portKey);
};

const removeSmartSelectModalBlocker = (node: go.Node | null | undefined) => {
  if (!node) {
    return;
  }

  const blocker = nodeSmartSelectModalBlockers.get(node);

  if (!blocker) {
    return;
  }

  const diagram = blocker.diagram;

  if (diagram) {
    diagram.commit(() => {
      diagram.remove(blocker);
    }, 'remove smart select modal blocker');
  }

  nodeSmartSelectModalBlockers.delete(node);
};

const releaseSmartSelectCanvasLock = (node: go.Node | null | undefined) => {
  if (!node) {
    return;
  }

  const releaseCanvasLock = nodeSmartSelectModalLockReleases.get(node);

  if (!releaseCanvasLock) {
    return;
  }

  releaseCanvasLock();
  nodeSmartSelectModalLockReleases.delete(node);
};

const removeSmartSelectOptionsPanel = (node: go.Node | null | undefined) => {
  if (!node) {
    return;
  }

  releaseSmartSelectCanvasLock(node);
  removeSmartSelectModalBlocker(node);
  removeSmartSelectPlacementPreview(node);
  nodeSmartSelectSelectedOptions.delete(node);
  nodeSmartSelectSelectedPortKeys.delete(node);

  const panel = nodeSmartSelectPanels.get(node);

  if (!panel) {
    return;
  }

  smartSelectPanelDragOffsets.delete(panel);

  const diagram = panel.diagram;

  if (diagram) {
    diagram.commit(() => {
      diagram.remove(panel);
    }, 'remove smart select options panel');
  }

  nodeSmartSelectPanels.delete(node);
};

const isPointerOverPort = (
  port: go.GraphObject,
  point: go.Point,
  padding: number,
) => {
  if (containsPointWithPadding(port.getDocumentBounds(), point, padding)) {
    return true;
  }

  const portPanelBounds = port.panel?.getDocumentBounds();

  return portPanelBounds
    ? containsPointWithPadding(portPanelBounds, point, padding)
    : false;
};

const isPointInFreePortHoverSpace = (node: go.Node, point: go.Point) => {
  if (!isRealPoint(point)) {
    return false;
  }

  if (containsPointWithPadding(node.actualBounds, point, HOVER_HIT_PADDING)) {
    return true;
  }

  const button = nodeSmartSelectButtons.get(node);

  if (
    button &&
    containsPointWithPadding(button.actualBounds, point, HOVER_HIT_PADDING)
  ) {
    return true;
  }

  const smartSelectPanel = nodeSmartSelectPanels.get(node);

  if (
    smartSelectPanel &&
    containsPointWithPadding(
      smartSelectPanel.actualBounds,
      point,
      HOVER_HIT_PADDING,
    )
  ) {
    return true;
  }

  const placementPreviews = nodeSmartSelectPlacementPreviews.get(node);

  if (
    placementPreviews &&
    [...placementPreviews.values()].some((preview) =>
      containsPointWithPadding(preview.actualBounds, point, HOVER_HIT_PADDING),
    )
  ) {
    return true;
  }

  const placementArrows = nodeSmartSelectPlacementArrows.get(node);

  if (
    placementArrows &&
    [...placementArrows.values()].some((arrow) =>
      containsPointWithPadding(arrow.actualBounds, point, HOVER_HIT_PADDING),
    )
  ) {
    return true;
  }

  const arrows = nodeArrows.get(node);

  if (
    arrows?.some((arrow) =>
      containsPointWithPadding(arrow.actualBounds, point, HOVER_HIT_PADDING),
    )
  ) {
    return true;
  }

  let isOverPort = false;
  node.ports.each((port) => {
    if (isPointerOverPort(port, point, HOVER_HIT_PADDING)) {
      isOverPort = true;
    }
  });

  return isOverPort;
};

const isPointerInFreePortHoverSpace = (node: go.Node) => {
  const point = node.diagram?.lastInput.documentPoint;

  return point ? isPointInFreePortHoverSpace(node, point) : false;
};

const getHoverPort = (obj: go.GraphObject) => {
  if (obj.portId) {
    return obj;
  }

  if (obj instanceof go.Panel) {
    return obj.findObject('OUTPUTPORT') ?? obj.findObject('INPUTPORT');
  }

  return null;
};

const startSmartSelectPanelDrag = (
  node: go.Node,
  event: go.InputEvent,
  obj: go.GraphObject,
) => {
  const panel = obj.part;

  if (!(panel instanceof go.Part)) {
    return;
  }

  clearTimer(hideTimers, node);
  smartSelectPanelDragOffsets.set(
    panel,
    new go.Point(
      event.documentPoint.x - panel.position.x,
      event.documentPoint.y - panel.position.y,
    ),
  );
  event.handled = true;
};

const moveSmartSelectPanel = (
  node: go.Node,
  event: go.InputEvent,
  obj: go.GraphObject,
) => {
  const panel = obj.part;
  const diagram = panel?.diagram;

  if (!(panel instanceof go.Part) || !diagram) {
    return;
  }

  const offset = smartSelectPanelDragOffsets.get(panel);

  if (!offset) {
    return;
  }

  clearTimer(hideTimers, node);
  const panelDimensions = getSmartSelectPanelDimensions(diagram);
  panel.position = clampPointToViewport(
    new go.Point(
      event.documentPoint.x - offset.x,
      event.documentPoint.y - offset.y,
    ),
    diagram,
    panelDimensions.width,
    panelDimensions.height,
  );
  event.handled = true;
};

const stopSmartSelectPanelDrag = (
  event: go.InputEvent,
  obj: go.GraphObject,
) => {
  const panel = obj.part;

  if (panel instanceof go.Part) {
    smartSelectPanelDragOffsets.delete(panel);
  }

  event.handled = true;
};

const previewSmartSelectOption = (
  node: go.Node,
  option: SmartSelectRecommendedOption,
) => {
  const selectedPortKey = nodeSmartSelectSelectedPortKeys.get(node);
  const preview = selectedPortKey
    ? nodeSmartSelectPlacementPreviews.get(node)?.get(selectedPortKey)
    : undefined;
  const previewImage = preview?.findObject(
    SMART_SELECT_PLACEMENT_PREVIEW_IMAGE_NAME,
  );

  clearTimer(hideTimers, node);

  if (!(previewImage instanceof go.Picture)) {
    return;
  }

  previewImage.source = option.imageSource ?? '';
  previewImage.visible = Boolean(option.imageSource);
  if (!selectedPortKey) {
    return;
  }

  let selectedOptions = nodeSmartSelectSelectedOptions.get(node);
  if (!selectedOptions) {
    selectedOptions = new Map<string, SmartSelectRecommendedOption>();
    nodeSmartSelectSelectedOptions.set(node, selectedOptions);
  }
  selectedOptions.set(selectedPortKey, option);
  setSmartSelectUseAndCloseEnabled(node, true);
};

const updateSmartSelectPlacement = (node: go.Node, port: go.GraphObject) => {
  const diagram = node.diagram;

  if (!diagram) {
    return;
  }

  const portKey = port.portId;
  const selectedOption = nodeSmartSelectSelectedOptions.get(node)?.get(portKey);
  const placementArrow = createSmartSelectPlacementArrow(node, port);
  const placementPreview = createSmartSelectPlacementPreview(node, port);

  const previews = nodeSmartSelectPlacementPreviews.get(node) ?? new Map();
  const arrows = nodeSmartSelectPlacementArrows.get(node) ?? new Map();
  const previousPreview = previews.get(portKey);
  const previousArrow = arrows.get(portKey);

  diagram.commit(() => {
    if (previousArrow) {
      diagram.remove(previousArrow);
    }
    if (previousPreview) {
      diagram.remove(previousPreview);
    }
    if (placementArrow) {
      diagram.add(placementArrow);
    }
    diagram.add(placementPreview);
  }, 'update smart select output placement');

  if (placementArrow) {
    arrows.set(portKey, placementArrow);
  } else {
    arrows.delete(portKey);
  }
  previews.set(portKey, placementPreview);
  nodeSmartSelectPlacementArrows.set(node, arrows);
  nodeSmartSelectPlacementPreviews.set(node, previews);

  if (selectedOption) {
    previewSmartSelectOption(node, selectedOption);
  }
};

const restoreSmartSelectSourceSelection = (
  diagram: go.Diagram,
  sourceUnitKey: string,
  addedUnitKeys: string[],
  attemptsRemaining = 10,
) => {
  const addedUnitsAreRendered = addedUnitKeys.every((unitKey) =>
    diagram.findNodeForKey(unitKey),
  );

  if (!addedUnitsAreRendered && attemptsRemaining > 0) {
    window.requestAnimationFrame(() =>
      restoreSmartSelectSourceSelection(
        diagram,
        sourceUnitKey,
        addedUnitKeys,
        attemptsRemaining - 1,
      ),
    );
    return;
  }

  const sourceNode = diagram.findNodeForKey(sourceUnitKey);
  if (sourceNode) {
    diagram.select(sourceNode);
  }
  store.dispatch(
    selectNode({
      nodeKey: sourceUnitKey,
      unitKey: sourceUnitKey,
      multiSelect: false,
    }),
  );
};

const commitSelectedSmartSelectOption = (node: go.Node) => {
  const selectedOptions = nodeSmartSelectSelectedOptions.get(node);
  const previews = nodeSmartSelectPlacementPreviews.get(node);
  const sourceUnitKey = node.data?.key?.toString();

  if (!selectedOptions?.size || !previews || !sourceUnitKey) {
    return;
  }

  const allUnitInfoData = store.getState().unitInfoDataState.unitInfoData;
  const addedUnitKeys: string[] = [];
  selectedOptions.forEach((option, portKey) => {
    const selectedPort = node.findPort(portKey);
    const preview = previews.get(portKey);
    const sourcePort = selectedPort && getPortData(node, selectedPort);

    if (
      !selectedPort ||
      !preview ||
      !isSmartSelectableOutputPort(node, sourcePort)
    ) {
      return;
    }

    const unitInfoData = getUnitInfoData(option.unitType, allUnitInfoData);
    const unitKey = uuidv4();
    addedUnitKeys.push(unitKey);
    const inputPortKey = uuidv4();
    const previewCenter = getRectCenter(preview.actualBounds);
    const arrowPoints = getPlacementArrowPoints(node, selectedPort);
    const connectionPoints = arrowPoints
      ? [
          { x: arrowPoints.start.x, y: arrowPoints.start.y },
          { x: arrowPoints.end.x, y: arrowPoints.end.y },
        ]
      : undefined;
    const layout: GoJSInterface = {
      category: option.unitType,
      loc: go.Point.stringify(previewCenter),
      mirrored: false,
      hideNonMajorWarnings: false,
      ...getPortsForUnit(option.unitType, option.variantId),
      ...(option.variantId ? { variantId: option.variantId } : {}),
    };

    store.dispatch(
      addUnit(
        layout,
        unitInfoData,
        unitKey,
        undefined,
        [{ unitKey, key: inputPortKey }],
        createSmartSelectUnitInfo(option, unitInfoData),
      ),
    );
    store.dispatch(
      addConnection(
        {
          from: sourceUnitKey,
          to: unitKey,
          fromPort: sourcePort.key,
          toPort: inputPortKey,
        },
        undefined,
        connectionPoints,
      ),
    );
  });

  const diagram = node.diagram;
  removeSmartSelectOptionsPanel(node);
  store.dispatch(
    selectNode({
      nodeKey: sourceUnitKey,
      unitKey: sourceUnitKey,
      multiSelect: false,
    }),
  );
  if (diagram) {
    restoreSmartSelectSourceSelection(diagram, sourceUnitKey, addedUnitKeys);
  }
};

const toggleSmartSelectOption = (
  node: go.Node,
  option: SmartSelectRecommendedOption,
) => {
  const activePortKey = nodeSmartSelectSelectedPortKeys.get(node);
  if (!activePortKey) {
    return;
  }

  const selectedOptions = nodeSmartSelectSelectedOptions.get(node);
  if (selectedOptions?.get(activePortKey) === option) {
    selectedOptions.delete(activePortKey);
    const activePort = node.findPort(activePortKey);
    if (activePort) {
      updateSmartSelectPlacement(node, activePort);
    }
    setSmartSelectUseAndCloseEnabled(node, selectedOptions.size > 0);
  } else {
    previewSmartSelectOption(node, option);
  }

  updateSmartSelectRecommendedOptions(node);
};

function selectSmartSelectOutputPort(node: go.Node, portKey: string) {
  const port = node.findPort(portKey);

  if (!port) {
    return;
  }

  const portData = getPortData(node, port);

  if (!isSmartSelectableOutputPort(node, portData)) {
    return;
  }

  const previousPortKey = nodeSmartSelectSelectedPortKeys.get(node);
  if (
    previousPortKey &&
    previousPortKey !== portKey &&
    !nodeSmartSelectSelectedOptions.get(node)?.has(previousPortKey)
  ) {
    removeSmartSelectPlacementForPort(node, previousPortKey);
  }

  nodeSmartSelectSelectedPortKeys.set(node, portKey);
  setSmartSelectUseAndCloseEnabled(
    node,
    Boolean(nodeSmartSelectSelectedOptions.get(node)?.size),
  );
  updateSmartSelectOutputPills(node);
  updateSmartSelectParticleSize(node, portData);
  updateSmartSelectPlacement(node, port);
  loadSmartSelectRecommendations(
    node,
    portData,
    updateSmartSelectRecommendedOptions,
  );
}

const showSmartSelectOptionsPanel = (node: go.Node, port: go.GraphObject) => {
  const diagram = node.diagram;
  const portData = getPortData(node, port);

  if (!diagram || !isSmartSelectableOutputPort(node, portData)) {
    return;
  }

  store.dispatch(setActiveUnitError(false));
  store.dispatch(setAdditionalActiveUnitError(false));
  store.dispatch(setActiveUnit(null));
  store.dispatch(setOpenPanel(null));
  store.dispatch(setShowSideCard(false));
  store.dispatch(setShowReportPanel(false));
  store.dispatch(clearSelectedNodes());
  removeSmartSelectOptionsPanel(node);
  removeSmartSelectButton(node);
  removeFreePortArrows(node);
  nodeSmartSelectSelectedPortKeys.set(node, port.portId);
  nodeSmartSelectModalLockReleases.set(node, lockCanvasForModal(diagram));

  const modalBlocker = createCanvasModalBlocker(diagram, {
    onBlockedInput: () => {
      clearTimer(hideTimers, node);
    },
  });
  const placementArrow = createSmartSelectPlacementArrow(node, port);
  const placementPreview = createSmartSelectPlacementPreview(node, port);
  const panel = createSmartSelectOptionsPanel(node, port);

  diagram.commit(() => {
    diagram.add(modalBlocker);
    if (placementArrow) {
      diagram.add(placementArrow);
    }
    diagram.add(placementPreview);
    diagram.add(panel);
  }, 'show smart select options panel');

  if (placementArrow) {
    nodeSmartSelectPlacementArrows.set(
      node,
      new Map([[port.portId, placementArrow]]),
    );
  }
  nodeSmartSelectModalBlockers.set(node, modalBlocker);
  nodeSmartSelectPlacementPreviews.set(
    node,
    new Map([[port.portId, placementPreview]]),
  );
  nodeSmartSelectPanels.set(node, panel);
  loadSmartSelectRecommendations(
    node,
    portData,
    updateSmartSelectRecommendedOptions,
  );
};

export const showFreePortSmartSelectButton = (
  obj: go.GraphObject | null | undefined,
) => {
  if (!checkFeatureFlag(FeatureFlag.ENABLE_SMART_SELECT)) {
    return;
  }

  const node = obj?.part;

  if (!(node instanceof go.Node) || !node.isSelected || !node.diagram || !obj) {
    return;
  }

  if (node.diagram.toolManager.draggingTool.isActive) {
    return;
  }

  if (nodeSmartSelectPanels.has(node)) {
    return;
  }

  const port = getHoverPort(obj);

  if (!port) {
    return;
  }

  const portData = getPortData(node, port);

  if (!isSmartSelectableOutputPort(node, portData)) {
    return;
  }

  clearTimer(hideTimers, node);

  if (nodeSmartSelectPorts.get(node) === port.portId) {
    return;
  }

  const button = createSmartSelectButton(node, port);

  if (!button) {
    return;
  }

  removeSmartSelectButton(node);
  node.diagram.add(button);
  nodeSmartSelectButtons.set(node, button);
  nodeSmartSelectPorts.set(node, port.portId);
};

export const keepFreePortHoverArrowsAlive = (
  obj: go.GraphObject | null | undefined,
) => {
  const node = obj?.part;

  if (node instanceof go.Node) {
    clearTimer(hideTimers, node);
  }
};

function scheduleFreePortHoverArrowsRemoval(node: go.Node | null | undefined) {
  if (!node) {
    return;
  }

  if (nodeSmartSelectPanels.has(node)) {
    clearTimer(hideTimers, node);
    return;
  }

  clearTimer(hideTimers, node);
  hideTimers.set(
    node,
    window.setTimeout(() => {
      if (isPointerInFreePortHoverSpace(node)) {
        clearTimer(hideTimers, node);
        return;
      }

      removeFreePortHoverArrows(node);
    }, HOVER_HIDE_DELAY_MS),
  );
}

const createArrow = (node: go.Node, port: go.GraphObject) => {
  const arrowPoints = getArrowPoints(node, port);

  if (!arrowPoints) {
    return null;
  }

  const arrow = createFreePortArrowLink(arrowPoints, {
    mouseEnter: () => {
      clearTimer(hideTimers, node);
      showFreePortSmartSelectButton(port);
    },
    mouseLeave: (event: go.InputEvent) => {
      if (isPointInFreePortHoverSpace(node, event.documentPoint)) {
        clearTimer(hideTimers, node);
        return;
      }

      scheduleFreePortHoverArrowsRemoval(node);
    },
  });

  return arrow;
};

function removeFreePortArrows(node: go.Node | null | undefined) {
  if (!node) {
    return;
  }

  const arrows = nodeArrows.get(node);

  if (!arrows) {
    return;
  }

  arrows.forEach((arrow) => arrow.diagram?.remove(arrow));
  nodeArrows.delete(node);
}

export function removeFreePortHoverArrows(node: go.Node | null | undefined) {
  if (!node) {
    return;
  }

  clearTimer(hideTimers, node);
  removeSmartSelectOptionsPanel(node);
  removeSmartSelectButton(node);
  removeFreePortArrows(node);
}

export const hideFreePortHoverArrows = (node: go.Node | null | undefined) => {
  if (node) {
    hoveredNodes.delete(node);
  }

  scheduleFreePortHoverArrowsRemoval(node);
};

export const showFreePortHoverArrows = (node: go.Node | null | undefined) => {
  if (!node || node.diagram?.toolManager.draggingTool.isActive) {
    return;
  }

  hoveredNodes.add(node);
  clearTimer(hideTimers, node);
  removeFreePortArrows(node);

  if (nodeSmartSelectPanels.has(node)) {
    return;
  }

  if (!node.isSelected || !node.diagram) {
    return;
  }

  const arrows: go.Link[] = [];
  node.ports.each((port) => {
    const portData = getPortData(node, port);

    if (!isSmartSelectableOutputPort(node, portData)) {
      return;
    }

    const arrow = createArrow(node, port);

    if (!arrow) {
      return;
    }

    node.diagram?.add(arrow);
    arrows.push(arrow);
  });

  if (arrows.length > 0) {
    nodeArrows.set(node, arrows);
  }
};

export const handleFreePortArrowSelectionChanged = (node: go.Part) => {
  if (!(node instanceof go.Node)) {
    return;
  }

  if (!node.isSelected) {
    if (nodeSmartSelectPanels.has(node)) {
      clearTimer(hideTimers, node);
      removeSmartSelectButton(node);
      removeFreePortArrows(node);
      return;
    }

    removeFreePortHoverArrows(node);
    return;
  }

  if (hoveredNodes.has(node)) {
    showFreePortHoverArrows(node);
  }
};

const {
  setSmartSelectUseAndCloseEnabled,
  updateSmartSelectOutputPills,
  updateSmartSelectParticleSize,
  updateSmartSelectRecommendedOptions,
  createSmartSelectOptionsPanel,
  createSmartSelectButton,
} = createSmartSelectPanelView({
  selectSmartSelectOutputPort,
  commitSelectedSmartSelectOption,
  scheduleFreePortHoverArrowsRemoval,
  startSmartSelectPanelDrag,
  moveSmartSelectPanel,
  stopSmartSelectPanelDrag,
  removeSmartSelectOptionsPanel,
  showSmartSelectOptionsPanel,
  toggleSmartSelectOption,
});
